import { useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { api, pesanGalat } from "../../../lib/api";
import { useAuth } from "../../../context/AuthContext";
import { useModalLayer } from "../../../hooks/useModalGuard";

/** Bagian bersama layar Claim MDR (brief owner 22 Sep 2026 bagian C, C1-C16).
 *
 *  KONSTANTA = cermin `backend/routers/claim_mdr_mesin.py`. Nilainya dikirim ke
 *  API (filter status) dan menentukan tombol mana yang muncul per baris, jadi
 *  kalau backend menambah status, tambahkan DI SINI saja — jebakan "status baru
 *  mematikan penjaga yang menyaring literal" (CLAUDE.md) berlaku juga di layar.
 *  Kesamaannya ditegakkan `backend/tests/test_claim_mdr_layar.py`. */

export const MDR_BASE = "/claim-mdr";
// Kunci izin — SAMA dengan `MODUL_IZIN` backend (aturan #3: tombol yang tampil
// memakai kunci yang sama dengan `require_permission` endpoint-nya).
export const IZIN_MDR = "finance_claim_mdr";
export const RUTE_CLAIM_MDR = "/finance/claim-mdr";

// Status tampilan (dihitung mesin) + status persisten klaim.
export const ST_ELIGIBLE = "eligible";
export const ST_TIDAK_ELIGIBLE = "tidak_eligible";
export const ST_PERLU_REVIEW = "perlu_review";
export const ST_DATA_DUPLIKAT = "data_duplikat";
export const ST_SIAP = "siap_claim";
export const ST_SUDAH = "sudah_claim";
export const STATUS_TAMPIL = [ST_ELIGIBLE, ST_TIDAK_ELIGIBLE, ST_PERLU_REVIEW, ST_DATA_DUPLIKAT, ST_SIAP, ST_SUDAH];

// Status batch ekspor.
export const BATCH_DIEKSPOR = "diekspor";
export const BATCH_SUDAH = "sudah_diklaim";
export const BATCH_DIBATALKAN = "dibatalkan";

export const STATUS_META = {
  [ST_ELIGIBLE]:       { label: "Eligible",       cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  [ST_TIDAK_ELIGIBLE]: { label: "Tidak Eligible", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  [ST_PERLU_REVIEW]:   { label: "Perlu Review",   cls: "bg-amber-50 text-amber-800 border-amber-200" },
  [ST_DATA_DUPLIKAT]:  { label: "Data Duplikat",  cls: "bg-violet-50 text-violet-700 border-violet-200" },
  [ST_SIAP]:           { label: "Siap Claim",     cls: "bg-sky-50 text-sky-700 border-sky-200" },
  [ST_SUDAH]:          { label: "Sudah Claim",    cls: "bg-slate-800 text-white border-slate-800" },
};

export const BATCH_META = {
  [BATCH_DIEKSPOR]:   { label: "Diekspor",       cls: "bg-sky-50 text-sky-700 border-sky-200" },
  [BATCH_SUDAH]:      { label: "Sudah Diklaim",  cls: "bg-slate-800 text-white border-slate-800" },
  [BATCH_DIBATALKAN]: { label: "Dibatalkan",     cls: "bg-slate-100 text-slate-500 border-slate-200" },
};

export const KELOMPOK_LABEL = { prioritas: "Prioritas", non_prioritas: "Non-prioritas", cc: "Kartu Kredit" };

export const MIME_XLSX = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

/** Hak tombol dari `can()` dengan kunci & aksi yang SAMA dengan backend:
 *  view = semua GET, create = impor, update = klaim/batch/pengaturan/nonaktif,
 *  delete = hapus impor & SETUJUI batal Sudah Claim (SPV). Catat CN juga butuh
 *  izin Credit Note create+update — persis `claim_mdr.py::catat_cn_batch`,
 *  yang menegakkannya sendiri karena memanggil fungsi CN langsung. */
export function useHakMdr() {
  const { can } = useAuth();
  const ubah = can(IZIN_MDR, "update");
  return {
    lihat: can(IZIN_MDR, "view"),
    impor: can(IZIN_MDR, "create"),
    ubah,
    hapus: can(IZIN_MDR, "delete"),
    catatCn: ubah && can("finance_credit_note", "create") && can("finance_credit_note", "update"),
  };
}

export function StatusPill({ status, label, testid }) {
  const m = STATUS_META[status] || { label: label || status || "-", cls: "bg-slate-50 text-slate-600 border-slate-200" };
  return (
    <span data-testid={testid}
          className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-medium whitespace-nowrap ${m.cls}`}>
      {label || m.label}
    </span>
  );
}

export function BatchPill({ status }) {
  const m = BATCH_META[status] || { label: status || "-", cls: "bg-slate-50 text-slate-600 border-slate-200" };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-medium ${m.cls}`}>{m.label}</span>;
}

/** Unduhan berkas lewat axios (butuh header Authorization) — nama berkas dari
 *  `Content-Disposition` server kalau ada, supaya nomor batch ikut. */
export async function unduhBerkas(url, namaCadangan, params) {
  const res = await api.get(url, { responseType: "blob", params });
  const cd = res.headers?.["content-disposition"] || "";
  const m = /filename="?([^";]+)"?/i.exec(cd);
  const blob = new Blob([res.data], { type: res.headers?.["content-type"] || MIME_XLSX });
  const objUrl = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objUrl;
  a.download = (m && m[1]) || namaCadangan;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(objUrl);
}

/** Galat unduhan `responseType: blob` datang sebagai Blob — `detail`-nya harus
 *  dibaca dulu, kalau tidak toast hanya berbunyi "Gagal" tanpa sebab. */
export async function pesanGalatUnduh(e, baku) {
  const data = e?.response?.data;
  if (data instanceof Blob) {
    try {
      const j = JSON.parse(await data.text());
      if (typeof j?.detail === "string") return j.detail;
    } catch { /* bukan JSON */ }
  }
  return pesanGalat(e, baku);
}

export async function unduhDenganToast(url, nama, params, baku = "Gagal mengunduh berkas") {
  try { await unduhBerkas(url, nama, params); }
  catch (e) { toast.error(await pesanGalatUnduh(e, baku)); }
}

/** Kerangka modal sederhana. `useModalLayer` supaya Escape/Ctrl+Enter yang
 *  ditujukan ke dialog di atasnya tidak ikut memicu modal lain di baliknya. */
export function Modal({ judul, sub, onTutup, children, lebar = "max-w-2xl", testid, ikon: Ikon }) {
  useModalLayer(true);
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onTutup}>
      <div onClick={(e) => e.stopPropagation()} data-testid={testid}
           className={`bg-white rounded-2xl ${lebar} w-full shadow-2xl max-h-[92vh] flex flex-col`}>
        <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-3 border-b border-slate-100">
          <div className="min-w-0">
            <h3 className="font-display font-bold text-lg text-slate-900 flex items-center gap-2">
              {Ikon && <Ikon className="w-5 h-5 text-emerald-600 shrink-0" />} {judul}
            </h3>
            {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
          </div>
          <button type="button" onClick={onTutup} data-testid={testid ? `${testid}-tutup` : undefined}
                  className="p-1.5 hover:bg-slate-100 rounded-lg shrink-0"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-5 py-4 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

/** Dialog isian alasan/catatan — dipakai batal Siap, ajukan/tolak batal,
 *  tinjau selesai, batalkan batch, hapus impor, nonaktifkan SRP. Backend
 *  menolak alasan kosong di aksi yang mewajibkannya; `wajib` hanya menahan
 *  tombol supaya staf tidak menekan sesuatu yang pasti ditolak. */
export function DialogAlasan({ judul, keterangan, label = "Alasan", labelYa = "Simpan", wajib = true,
                               bahaya = false, onYa, onTutup, testid = "mdr-alasan" }) {
  const [teks, setTeks] = useState("");
  const [busy, setBusy] = useState(false);
  useModalLayer(true);
  const kirim = async () => {
    setBusy(true);
    try { await onYa(teks.trim()); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/50 grid place-items-center p-4" onClick={busy ? undefined : onTutup}>
      <div onClick={(e) => e.stopPropagation()} data-testid={testid}
           className="bg-white rounded-2xl max-w-md w-full shadow-2xl p-5 space-y-3">
        <h3 className="font-display font-bold text-base text-slate-900">{judul}</h3>
        {keterangan && <div className="text-sm text-slate-600">{keterangan}</div>}
        <label className="block">
          <span className="text-xs font-medium text-slate-600">{label}{wajib ? " *" : ""}</span>
          <textarea value={teks} onChange={(e) => setTeks(e.target.value)} rows={3} data-testid={`${testid}-teks`}
                    className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:border-emerald-500 outline-none" />
        </label>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onTutup} disabled={busy} data-testid={`${testid}-batal`}
                  className="h-10 px-4 rounded-lg border border-slate-200 text-sm text-slate-700 disabled:opacity-50">
            Batal
          </button>
          <button type="button" onClick={kirim} disabled={busy || (wajib && !teks.trim())} data-testid={`${testid}-ya`}
                  className={`h-10 px-4 rounded-lg text-white text-sm font-medium disabled:opacity-50 ${
                    bahaya ? "bg-rose-600 hover:bg-rose-700" : "bg-emerald-600 hover:bg-emerald-700"}`}>
            {busy ? "Memproses..." : labelYa}
          </button>
        </div>
      </div>
    </div>
  );
}

export const kelasInput = "h-10 px-3 border border-slate-200 rounded-lg text-sm focus:border-emerald-500 outline-none bg-white";
export const kelasTombol = "h-10 px-3 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-50";
export const kelasTombolUtama = "h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-50";
export const kelasTombolKecil = "h-8 px-2.5 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 text-xs font-medium inline-flex items-center gap-1 disabled:opacity-50";
